import type { Plan, RunState } from '../v4/contracts'
import type { PlanCard } from '../v4/contracts/card-types'
import { eventLine } from '../v4/conversation-text'
import { planCardOf } from '../v4/PlanCard'
import { valueText } from './evidenceText'

/**
 * The run's conversation as the old chat told it (Figma "3. Create Workflow" and "8. Report"):
 * a card for each moment the engineer acts on or reads from (a plan to approve, the steps as they
 * run, the answer), an ask card where the engine asked, and one line in the engine's own words
 * (v4/conversation-text.ts) for everything else. Pure, so the story a run tells is testable apart
 * from React.
 */
export type StoryItem =
  | { kind: 'line'; seq: number; text: string }
  | { kind: 'ask'; seq: number; askId: string }
  | { kind: 'plan'; seq: number; plan: Plan; card: PlanCard | null }
  | { kind: 'steps'; seq: number; plan: Plan }
  | { kind: 'result'; seq: number; text: string | null }

export function runStory(run: RunState): StoryItem[] {
  const items: StoryItem[] = []
  const planOf = (version: number) => run.trace.planVersions.find((p) => p.version === version)
  const line = (e: RunState['events'][number]) => {
    const text = eventLine(e, run)
    if (text) items.push({ kind: 'line', seq: e.seq, text })
  }
  // One steps card per approved plan version, however many times the engine says it approved it.
  const stepsShown = new Set<number>()
  for (const e of run.events) {
    if (e.type === 'ask_opened') {
      const askId = e.payload?.askId
      if (typeof askId === 'string') items.push({ kind: 'ask', seq: e.seq, askId })
    } else if (e.type === 'plan_proposed') {
      const plan = planOf(e.planVersion)
      if (plan) items.push({ kind: 'plan', seq: e.seq, plan, card: planCardOf(run.events, e.planVersion) })
      else line(e)
    } else if (e.type === 'plan_approved') {
      line(e)
      const plan = planOf(e.planVersion)
      if (plan && !stepsShown.has(plan.version)) {
        stepsShown.add(plan.version)
        items.push({ kind: 'steps', seq: e.seq, plan })
      }
    } else if (e.type === 'finished') {
      items.push({ kind: 'result', seq: e.seq, text: eventLine(e, run) })
    } else {
      line(e)
    }
  }
  return items
}

/** The badge a run's outcome wears on the result card (an IntuiBadge variant). */
export function outcomeTone(outcome: string | undefined): 'success' | 'warning' | 'destructive' | 'outline' {
  switch (outcome) {
    case 'satisfied':
      return 'success'
    case 'partial':
    case 'refused':
      return 'warning'
    case 'blocked':
    case 'unsafe':
    case 'guardrail_stopped':
      return 'destructive'
    default:
      return 'outline'
  }
}

export type PlanSourceKey = 'plan_from_catalog' | 'plan_from_file' | 'plan_from_interview' | 'plan_from_question' | 'plan_from_data'

/** Where the plan came from, as the `workflow:builder.*` key that says it in words. */
export function planSourceKey(plan: Plan): PlanSourceKey {
  switch (plan.source.kind) {
    case 'catalog':
      return 'plan_from_catalog'
    case 'file':
      return 'plan_from_file'
    case 'interview':
      return 'plan_from_interview'
    case 'question':
      return 'plan_from_question'
    default:
      return 'plan_from_data'
  }
}

/** The evidence a finished step gave, in one phrase: its one value with its subject, or how many rows; null for none. */
export function stepEvidenceText(run: RunState, plan: Plan, stepId: string, rowsText: (count: number) => string): string | null {
  const traced = run.trace.steps.find((s) => s.stepId === stepId && s.planVersion === plan.version)
  const ids = new Set(traced?.evidenceIds ?? [])
  const rows = run.trace.evidence.filter((e) => ids.has(e.id))
  if (rows.length === 1) {
    const value = valueText(rows[0], '')
    return value ? `${rows[0].subject.display ?? rows[0].subject.id}: ${value}` : null
  }
  return rows.length > 1 ? rowsText(rows.length) : null
}

/**
 * Why a step stopped, from what the API already sends: the reason on its park or done event when
 * the engine gives one (with the outcome it names), else the error of its last failed call or the
 * reason its tool result carried (an organization admin gets the calls; others get none), else
 * null. The status label alone says "failed"; this says what failed.
 */
export function stepFailure(run: RunState, stepId: string): string | null {
  for (const e of [...run.events].reverse()) {
    if (e.stepId !== stepId) continue
    if (e.type !== 'step_parked' && e.type !== 'step_done') continue
    const p = e.payload ?? {}
    const outcome = typeof p.outcome === 'string' ? p.outcome : ''
    const reason = typeof p.reason === 'string' ? p.reason : ''
    if (reason) return outcome ? `${outcome}: ${reason}` : reason
    if (e.type === 'step_parked' && outcome) return outcome
    break
  }
  for (const c of [...run.calls].reverse()) {
    if (c.stepId !== stepId) continue
    if (c.error) return c.error
    const out = c.output as { outcome?: string; reason?: string } | undefined
    if (out?.outcome && out.outcome !== 'ok' && out.reason) return `${out.outcome}: ${out.reason}`
  }
  return null
}
